import React, { useState, useEffect } from 'react';
import { Terminal, Search, Play, Download, Loader2, Clock, AlertTriangle, ChevronDown } from 'lucide-react';
import { useLogGroups, useSimpleSearch, useInsightsQuery } from '../hooks/useLogs';

const TIME_PRESETS = [
  { label: '1 min', ms: 1 * 60 * 1000 },
  { label: '5 min', ms: 5 * 60 * 1000 },
  { label: '10 min', ms: 10 * 60 * 1000 },
  { label: '15 min', ms: 15 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
  { label: '6 hours', ms: 6 * 60 * 60 * 1000 },
  { label: '24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
];

const LOG_LEVELS = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const QUERY_TEMPLATES = [
  {
    name: 'Recent Errors',
    query: `fields @timestamp, @message, @logStream
| filter @message like /(?i)error/
| sort @timestamp desc
| limit 200`,
  },
  {
    name: 'API Latency > 1s',
    query: `fields @timestamp, @message, @duration
| filter @duration > 1000
| sort @duration desc
| limit 100`,
  },
  {
    name: 'Top Error Messages',
    query: `fields @message
| filter @message like /(?i)error/
| stats count(*) as errorCount by @message
| sort errorCount desc
| limit 20`,
  },
  {
    name: 'Error Rate by Hour',
    query: `filter @message like /(?i)error/
| stats count(*) as errors by bin(1h)
| sort bin(1h) desc`,
  },
  {
    name: 'Request Count by Status',
    query: `fields @message
| parse @message /(?<statusCode>\\d{3})/
| stats count(*) as requests by statusCode
| sort requests desc
| limit 20`,
  },
];

const formatTimestamp = (ts) => {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
};

const detectLogLevel = (message) => {
  if (!message) return '';
  const upper = message.toUpperCase();
  if (upper.includes('ERROR') || upper.includes('FATAL')) return 'ERROR';
  if (upper.includes('WARN')) return 'WARN';
  if (upper.includes('INFO')) return 'INFO';
  if (upper.includes('DEBUG')) return 'DEBUG';
  return '';
};

const exportToCsv = (data, filename) => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers.map((h) => {
      const val = String(row[h] || '').replace(/"/g, '""');
      return `"${val}"`;
    }).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function TechSupport() {
  const [activeTab, setActiveTab] = useState('simple');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [timePreset, setTimePreset] = useState(TIME_PRESETS[1]); // 1 hour default
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [keyword, setKeyword] = useState('');
  const [logLevel, setLogLevel] = useState('ALL');
  const [queryString, setQueryString] = useState(QUERY_TEMPLATES[0].query);
  const [results, setResults] = useState(null);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [filterGroup, setFilterGroup] = useState('ALL');

  const { data: logGroupsData } = useLogGroups();
  const simpleSearch = useSimpleSearch();
  const insightsQuery = useInsightsQuery();

  const logGroups = logGroupsData?.data || [];
  const isLoading = simpleSearch.isPending || insightsQuery.isPending;

  const [prefillApplied, setPrefillApplied] = useState(false);

  // Auto-select first group (or handle prefill)
  useEffect(() => {
    if (logGroups.length === 0) return;

    const raw = sessionStorage.getItem('logSearchPrefill');
    if (raw && !prefillApplied) {
      try {
        const prefill = JSON.parse(raw);
        sessionStorage.removeItem('logSearchPrefill');
        setPrefillApplied(true);

        if (prefill.keyword) setKeyword(prefill.keyword);
        if (prefill.selectAllGroups) {
          setSelectedGroups(logGroups.map((g) => g.name));
        }
        if (prefill.timePresetIndex != null && TIME_PRESETS[prefill.timePresetIndex]) {
          setTimePreset(TIME_PRESETS[prefill.timePresetIndex]);
        }
        if (prefill.autoSearch) {
          const preset = prefill.timePresetIndex != null ? TIME_PRESETS[prefill.timePresetIndex] : timePreset;
          const now = Date.now();
          const groups = prefill.selectAllGroups ? logGroups.map((g) => g.name) : selectedGroups;
          simpleSearch.mutate(
            {
              logGroups: groups,
              startTime: now - preset.ms,
              endTime: now,
              keyword: prefill.keyword || keyword,
              logLevel: 'ALL',
            },
            {
              onSuccess: (data) => {
                setResults({
                  rows: data.data.results,
                  count: data.data.resultCount,
                  type: 'simple',
                });
              },
            }
          );
        }
      } catch (e) {
        sessionStorage.removeItem('logSearchPrefill');
      }
    } else if (selectedGroups.length === 0) {
      setSelectedGroups([logGroups[0].name]);
    }
  }, [logGroups]);

  const getTimeRange = () => {
    if (timePreset.ms === null) {
      return {
        startTime: new Date(customStart).getTime(),
        endTime: new Date(customEnd).getTime(),
      };
    }
    const now = Date.now();
    return { startTime: now - timePreset.ms, endTime: now };
  };

  const handleSimpleSearch = () => {
    setFilterLevel('ALL');
    setFilterGroup('ALL');
    const { startTime, endTime } = getTimeRange();
    simpleSearch.mutate(
      { logGroups: selectedGroups, startTime, endTime, keyword, logLevel },
      {
        onSuccess: (data) => {
          setResults({
            rows: data.data.results,
            count: data.data.resultCount,
            type: 'simple',
          });
        },
      }
    );
  };

  const handleInsightsQuery = () => {
    setFilterLevel('ALL');
    setFilterGroup('ALL');
    const { startTime, endTime } = getTimeRange();
    insightsQuery.mutate(
      { logGroups: selectedGroups, startTime, endTime, queryString },
      {
        onSuccess: (data) => {
          setResults({
            rows: data.data.results,
            count: data.data.resultCount,
            stats: data.data.statistics,
            status: data.data.status,
            type: 'insights',
          });
        },
      }
    );
  };

  const error = simpleSearch.error || insightsQuery.error;

  const toggleGroup = (name) => {
    setSelectedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Terminal className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Log Search</h1>
            <p className="text-sm text-gray-500">Query CloudWatch logs for troubleshooting</p>
          </div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('simple')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'simple'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Search className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Simple Search
        </button>
        <button
          onClick={() => setActiveTab('insights')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'insights'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Terminal className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Insights Query
        </button>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        {/* Row 1: Log Groups + Time Range */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Log Group Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Log Groups</label>
            <div className="relative">
              <button
                onClick={() => setGroupsOpen(!groupsOpen)}
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left hover:border-gray-400"
              >
                <span className="text-gray-700">
                  {selectedGroups.length === 0
                    ? 'Select log groups...'
                    : `${selectedGroups.length} group${selectedGroups.length > 1 ? 's' : ''} selected`}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
              {groupsOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-80 overflow-y-auto">
                  <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-100">
                    <button
                      onClick={() => setSelectedGroups(logGroups.map((g) => g.name))}
                      className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedGroups([])}
                      className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                    >
                      Deselect All
                    </button>
                  </div>
                  {logGroups.map((g) => (
                    <label
                      key={g.name}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(g.name)}
                        onChange={() => toggleGroup(g.name)}
                        className="mr-2 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      />
                      <span className="text-sm text-gray-700 font-mono">{g.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              Time Range
            </label>
            <div className="flex flex-wrap gap-1.5">
              {TIME_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => setTimePreset(p)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    timePreset.label === p.label
                      ? 'bg-amber-100 text-amber-700 border border-amber-300'
                      : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={() => setTimePreset({ label: 'Custom', ms: null })}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  timePreset.ms === null
                    ? 'bg-amber-100 text-amber-700 border border-amber-300'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                Custom
              </button>
            </div>
            {timePreset.ms === null && (
              <div className="flex gap-2 mt-2">
                <input
                  type="datetime-local"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
                <span className="text-gray-400 self-center">to</span>
                <input
                  type="datetime-local"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Tab-specific controls */}
        {activeTab === 'simple' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Keyword</label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSimpleSearch()}
                placeholder="Search for keywords in log messages..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Log Level</label>
              <select
                value={logLevel}
                onChange={(e) => setLogLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {LOG_LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Insights Query</label>
              <select
                onChange={(e) => {
                  const tmpl = QUERY_TEMPLATES.find((t) => t.name === e.target.value);
                  if (tmpl) setQueryString(tmpl.query);
                }}
                defaultValue=""
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 bg-white"
              >
                <option value="" disabled>
                  Load template...
                </option>
                {QUERY_TEMPLATES.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              value={queryString}
              onChange={(e) => setQueryString(e.target.value)}
              rows={6}
              spellCheck={false}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-y"
            />
          </div>
        )}

        {/* Action Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={activeTab === 'simple' ? handleSimpleSearch : handleInsightsQuery}
            disabled={isLoading || selectedGroups.length === 0}
            className="px-5 py-2 bg-amber-400 text-gray-900 rounded-lg text-sm font-medium hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                {activeTab === 'simple' ? (
                  <Search className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                {activeTab === 'simple' ? 'Search' : 'Run Query'}
              </>
            )}
          </button>

          {results && results.rows.length > 0 && (
            <button
              onClick={() =>
                exportToCsv(results.rows, `cloudwatch-logs-${activeTab}`)
              }
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}

          {results && (
            <span className="text-sm text-gray-500">
              {results.count.toLocaleString()} result{results.count !== 1 ? 's' : ''}
              {results.stats && (
                <span className="ml-2 text-gray-400">
                  ({(results.stats.bytesScanned / 1024 / 1024).toFixed(1)} MB scanned)
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Query Failed</p>
            <p className="text-sm text-red-600 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {results && results.rows.length > 0 && (() => {
        const uniqueLevels = results.type === 'simple'
          ? [...new Set(results.rows.map((r) => detectLogLevel(r.message)).filter(Boolean))]
          : [];
        const uniqueGroups = results.type === 'simple'
          ? [...new Set(results.rows.map((r) => r.logGroup).filter(Boolean))]
          : [];
        const filteredRows = results.rows.filter((row) => {
          if (results.type !== 'simple') return true;
          if (filterLevel !== 'ALL' && detectLogLevel(row.message) !== filterLevel) return false;
          if (filterGroup !== 'ALL' && row.logGroup !== filterGroup) return false;
          return true;
        });

        return (<>
        {/* Result Filters */}
        {results.type === 'simple' && (uniqueLevels.length > 1 || uniqueGroups.length > 1) && (
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-4 flex-wrap">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Filter Results:</span>
            {uniqueLevels.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Level</label>
                <select
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-amber-400"
                >
                  <option value="ALL">All</option>
                  {uniqueLevels.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
            )}
            {uniqueGroups.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Log Group</label>
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-amber-400"
                >
                  <option value="ALL">All</option>
                  {uniqueGroups.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            )}
            {(filterLevel !== 'ALL' || filterGroup !== 'ALL') && (
              <button
                onClick={() => { setFilterLevel('ALL'); setFilterGroup('ALL'); }}
                className="text-xs text-amber-600 hover:text-amber-800 font-medium"
              >
                Clear Filters
              </button>
            )}
            <span className="text-xs text-gray-400 ml-auto">
              Showing {filteredRows.length.toLocaleString()} of {results.rows.length.toLocaleString()}
            </span>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {results.type === 'simple' ? (
                    <>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 w-44">Timestamp</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 w-20">Level</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 w-48">Log Group</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Message</th>
                    </>
                  ) : (
                    Object.keys(results.rows[0])
                      .filter((k) => k !== '@ptr')
                      .map((key) => (
                        <th key={key} className="text-left px-4 py-3 font-medium text-gray-600">
                          {key}
                        </th>
                      ))
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row, i) => {
                  if (results.type === 'simple') {
                    const level = detectLogLevel(row.message);
                    const isError = level === 'ERROR';
                    return (
                      <tr
                        key={i}
                        className={`hover:bg-gray-50 ${isError ? 'bg-red-50' : ''}`}
                      >
                        <td className="px-4 py-2.5 text-gray-500 font-mono text-xs whitespace-nowrap">
                          {formatTimestamp(row.timestamp)}
                        </td>
                        <td className="px-4 py-2.5">
                          {level && (
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                level === 'ERROR'
                                  ? 'bg-red-100 text-red-700'
                                  : level === 'WARN'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : level === 'INFO'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {level}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">
                          {row.logGroup}
                        </td>
                        <td className={`px-4 py-2.5 font-mono text-xs break-all ${isError ? 'text-red-700' : 'text-gray-700'}`}>
                          {row.message}
                        </td>
                      </tr>
                    );
                  }

                  // Insights results
                  const keys = Object.keys(row).filter((k) => k !== '@ptr');
                  const isError =
                    row['@message']?.toUpperCase().includes('ERROR') ||
                    row.message?.toUpperCase().includes('ERROR');
                  return (
                    <tr
                      key={i}
                      className={`hover:bg-gray-50 ${isError ? 'bg-red-50' : ''}`}
                    >
                      {keys.map((key) => (
                        <td
                          key={key}
                          className={`px-4 py-2.5 font-mono text-xs break-all ${
                            isError ? 'text-red-700' : 'text-gray-700'
                          }`}
                        >
                          {key === '@timestamp' ? formatTimestamp(row[key]) : row[key]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>);
      })()}

      {/* Empty state */}
      {results && results.rows.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No results found for your query.</p>
          <p className="text-gray-400 text-xs mt-1">Try adjusting the time range or search criteria.</p>
        </div>
      )}
    </div>
  );
}
